import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { SlidePanel } from "@/components/ui/slide-panel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ClipboardList,
  Plus,
  Edit,
  Trash2,
  Copy,
  Eye,
  Download,
  Upload,
  Search,
  Filter,
  Settings,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from "@/lib/supabaseClient";
import { useToast } from "@/components/ui/use-toast";
import { TemplateItemManager } from "./TemplateItemManager";
import { PipelineTemplates } from "./PipelineTemplates";
import { NotificationTemplateManagement } from "./NotificationTemplateManagement";
import { ContractTemplatesTab } from "@/components/contracts/templates/ContractTemplatesTab";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { FormField } from "@/components/ui/form-field";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Define types based on database schema
interface ChecklistTemplate {
  id: string;
  name: string;
  description?: string;
  category?: string;
  stage_name?: string;
  pipeline_id?: number;
  pipeline_name?: string;
  scope: "global" | "tenant" | "shared";
  configuration: any;
  usage_stats?: any;
  is_active: boolean;
  created_by?: string;
  owner_tenant_id: string;
  created_at: string;
  updated_at: string;
  total_items_count?: number;
}

interface Pipeline {
  id: number;
  name: string;
  pipeline_type?: string;
}

interface Stage {
  id: number;
  name: string;
  stage_order: number;
  pipeline_id: number;
}

interface TemplatesTabProps {
  onRefreshData?: () => void;
}

export const TemplatesTab = ({ onRefreshData }: TemplatesTabProps) => {
  const { toast } = useToast();

  // State for templates and data
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedPipeline, setSelectedPipeline] = useState<number | null>(null);

  // Panel states
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [isDeletePanelOpen, setIsDeletePanelOpen] = useState(false);

  const [selectedTemplate, setSelectedTemplate] =
    useState<ChecklistTemplate | null>(null);
  const [showItemManager, setShowItemManager] = useState(false);

  // Form state
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    description: "",
    category: "",
    stage_name: "",
    pipeline_id: null as number | null,
    scope: "tenant" as "global" | "tenant" | "shared",
  });

  // Load data on component mount
  useEffect(() => {
    fetchTemplates();
    fetchPipelines();
    fetchStages();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      // Only fetch basic template info for the list view
      const { data, error } = await supabase
        .from("checklist_templates_library")
        .select(
          `
          id,
          name,
          description,
          category,
          stage_name,
          pipeline_id,
          pipeline_name,
          scope,
          total_items_count,
          usage_stats,
          configuration,
          is_active,
          created_by,
          owner_tenant_id,
          created_at,
          updated_at
        `
        )
        .eq("is_active", true)
        .order("name");

      if (error) {
        console.error("Error fetching templates:", error);
        toast({
          title: "Error",
          description: "Failed to load templates.",
          variant: "destructive",
        });
        return;
      }

      setTemplates((data || []) as ChecklistTemplate[]);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast({
        title: "Error",
        description: "Failed to load templates.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPipelines = async () => {
    try {
      const { data, error } = await supabase
        .from("pipelines")
        .select("id, name, pipeline_type")
        .order("name");

      if (error) {
        console.error("Error fetching pipelines:", error);
        return;
      }

      setPipelines(data || []);
    } catch (error) {
      console.error("Error fetching pipelines:", error);
    }
  };

  const fetchStages = async () => {
    try {
      const { data, error } = await supabase
        .from("stages")
        .select("id, name, stage_order, pipeline_id")
        .order("pipeline_id, stage_order");

      if (error) {
        console.error("Error fetching stages:", error);
        return;
      }

      setStages(data || []);
    } catch (error) {
      console.error("Error fetching stages:", error);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      if (!newTemplate.name.trim()) {
        toast({
          title: "Error",
          description: "Template name is required.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from("checklist_templates_library")
        .insert({
          name: newTemplate.name,
          description: newTemplate.description,
          category: newTemplate.category || null,
          stage_name: newTemplate.stage_name || null,
          pipeline_id: newTemplate.pipeline_id,
          pipeline_name: newTemplate.pipeline_id
            ? pipelines.find((p) => p.id === newTemplate.pipeline_id)?.name
            : null,
          scope: newTemplate.scope,
          configuration: {
            items: [],
            default_settings: {
              auto_notify_pm_at_percent: 80,
              auto_move_stage_when_complete: false,
              estimated_completion_time_hours: 2,
            },
          },
          usage_stats: {
            times_applied: 0,
            avg_completion_rate: 0,
            last_used: null,
          },
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating template:", error);
        toast({
          title: "Error",
          description: "Failed to create template.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Template created successfully.",
      });

      setIsCreatePanelOpen(false);
      setNewTemplate({
        name: "",
        description: "",
        category: "",
        stage_name: "",
        pipeline_id: null,
        scope: "tenant",
      });
      fetchTemplates();
    } catch (error) {
      console.error("Error creating template:", error);
      toast({
        title: "Error",
        description: "Failed to create template.",
        variant: "destructive",
      });
    }
  };

  const handleEditTemplate = (template: ChecklistTemplate) => {
    setSelectedTemplate(template);
    setNewTemplate({
      name: template.name,
      description: template.description || "",
      category: template.category || "",
      stage_name: template.stage_name || "",
      pipeline_id: template.pipeline_id || null,
      scope: template.scope,
    });
    setIsEditPanelOpen(true);
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      const { error } = await supabase
        .from("checklist_templates_library")
        .update({
          name: newTemplate.name,
          description: newTemplate.description,
          category: newTemplate.category || null,
          stage_name: newTemplate.stage_name || null,
          pipeline_id: newTemplate.pipeline_id,
          pipeline_name: newTemplate.pipeline_id
            ? pipelines.find((p) => p.id === newTemplate.pipeline_id)?.name
            : null,
          scope: newTemplate.scope,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedTemplate.id);

      if (error) {
        console.error("Error updating template:", error);
        toast({
          title: "Error",
          description: "Failed to update template.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Template updated successfully.",
      });

      setIsEditPanelOpen(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error("Error updating template:", error);
      toast({
        title: "Error",
        description: "Failed to update template.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      const { error } = await supabase
        .from("checklist_templates_library")
        .delete()
        .eq("id", selectedTemplate.id);

      if (error) {
        console.error("Error deleting template:", error);
        toast({
          title: "Error",
          description: "Failed to delete template.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Template deleted successfully.",
      });

      setIsDeletePanelOpen(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast({
        title: "Error",
        description: "Failed to delete template.",
        variant: "destructive",
      });
    }
  };

  const handleOpenItemManager = async (template: ChecklistTemplate) => {
    try {
      // Fetch detailed template info only when opening the item manager
      const { data, error } = await supabase
        .from("checklist_templates_library")
        .select("*")
        .eq("id", template.id)
        .single();

      if (error) {
        console.error("Error fetching detailed template:", error);
        toast({
          title: "Error",
          description: "Failed to load template details.",
          variant: "destructive",
        });
        return;
      }

      setSelectedTemplate(data);
      setShowItemManager(true);
    } catch (error) {
      console.error("Error fetching detailed template:", error);
      toast({
        title: "Error",
        description: "Failed to load template details.",
        variant: "destructive",
      });
    }
  };

  const handleDuplicateTemplate = async (template: ChecklistTemplate) => {
    try {
      const { data, error } = await supabase
        .from("checklist_templates_library")
        .insert({
          name: `${template.name} (Copy)`,
          description: template.description,
          category: template.category,
          stage_name: template.stage_name,
          pipeline_id: template.pipeline_id,
          pipeline_name: template.pipeline_name,
          scope: "tenant", // Duplicates are always tenant-scoped
          configuration: template.configuration,
          usage_stats: {
            times_applied: 0,
            avg_completion_rate: 0,
            last_used: null,
          },
        })
        .select()
        .single();

      if (error) {
        console.error("Error duplicating template:", error);
        toast({
          title: "Error",
          description: "Failed to duplicate template.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Template duplicated successfully.",
      });

      fetchTemplates();
    } catch (error) {
      console.error("Error duplicating template:", error);
      toast({
        title: "Error",
        description: "Failed to duplicate template.",
        variant: "destructive",
      });
    }
  };

  // Filter templates based on search and filters
  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (template.description &&
        template.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory =
      selectedCategory === "all" ||
      !selectedCategory ||
      template.category === selectedCategory;
    const matchesPipeline =
      !selectedPipeline || template.pipeline_id === selectedPipeline;

    return matchesSearch && matchesCategory && matchesPipeline;
  });

  // Get unique categories for filter
  const categories = [
    ...new Set(templates.map((t) => t.category).filter(Boolean)),
  ];

  const getScopeBadge = (scope: string) => {
    const variants = {
      global: "default",
      tenant: "secondary",
      shared: "outline",
    } as const;

    return (
      <Badge variant={variants[scope as keyof typeof variants] || "secondary"}>
        {scope.charAt(0).toUpperCase() + scope.slice(1)}
      </Badge>
    );
  };

  const getUsageStats = (template: ChecklistTemplate) => {
    const stats = template.usage_stats || {};
    return {
      timesApplied: stats.times_applied || 0,
      avgCompletion: stats.avg_completion_rate || 0,
      lastUsed: stats.last_used,
    };
  };

  const getItemsCount = (template: ChecklistTemplate) => {
    // Calculate items count from configuration
    const config = template.configuration || {};
    const items = config.items || [];

    return items.length;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <p>Loading templates...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Templates</h2>
          <p className="text-muted-foreground">
            Manage and organize your templates for different pipeline stages and
            processes
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pipelines" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-muted/50 border border-border h-10 p-0.5">
          <TabsTrigger
            value="pipelines"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm flex items-center justify-center"
          >
            Pipeline Templates
          </TabsTrigger>
          <TabsTrigger
            value="contracts"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm flex items-center justify-center"
          >
            Contract Templates
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm !h-8 !px-3 !py-0 !my-0 !mx-0.5 !rounded-sm flex items-center justify-center"
          >
            Notification Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pipelines" className="space-y-6">
          <PipelineTemplates />
        </TabsContent>

        <TabsContent value="checklists-removed" className="space-y-6">
          {/* Checklist Templates Header */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-foreground">
                Checklist Templates
              </h3>
              <p className="text-muted-foreground">
                Manage and organize your checklist templates for different
                pipeline stages
              </p>
            </div>
            <Button onClick={() => setIsCreatePanelOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </div>

          {/* Filters and Search */}
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem
                    key={category}
                    value={category || "uncategorized"}
                  >
                    {category || "Uncategorized"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedPipeline?.toString() || "all"}
              onValueChange={(value) =>
                setSelectedPipeline(value === "all" ? null : parseInt(value))
              }
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Pipelines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pipelines</SelectItem>
                {pipelines.map((pipeline) => (
                  <SelectItem key={pipeline.id} value={pipeline.id.toString()}>
                    {pipeline.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Templates Table */}
          <div className="overflow-hidden border rounded-lg shadow-sm">
            <div className="overflow-x-auto">
              <Table className="min-w-full">
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="w-1/4 font-semibold text-muted-foreground">
                      Template Name
                    </TableHead>
                    <TableHead className="font-semibold text-muted-foreground">
                      Category
                    </TableHead>
                    <TableHead className="font-semibold text-muted-foreground">
                      Pipeline
                    </TableHead>
                    <TableHead className="font-semibold text-muted-foreground">
                      Stage
                    </TableHead>
                    <TableHead className="font-semibold text-muted-foreground">
                      Scope
                    </TableHead>
                    <TableHead className="font-semibold text-muted-foreground">
                      Items
                    </TableHead>
                    <TableHead className="font-semibold text-muted-foreground">
                      Usage
                    </TableHead>
                    <TableHead className="w-32 font-semibold text-muted-foreground">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="bg-card">
                  {filteredTemplates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium text-foreground">
                          No templates found
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {searchTerm || selectedCategory || selectedPipeline
                            ? "Try adjusting your filters."
                            : "Create your first template to get started."}
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTemplates.map((template) => {
                      const stats = getUsageStats(template);

                      return (
                        <TableRow key={template.id} className="hover:bg-muted">
                          <TableCell className="font-medium">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-help">
                                    <div className="text-sm font-medium text-foreground">
                                      {template.name}
                                    </div>
                                  </div>
                                </TooltipTrigger>
                                {template.description && (
                                  <TooltipContent>
                                    <p className="max-w-xs">
                                      {template.description}
                                    </p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-foreground">
                              {template.category || "Uncategorized"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-foreground">
                              {template.pipeline_name || "None"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-foreground">
                              {template.stage_name || "None"}
                            </span>
                          </TableCell>
                          <TableCell>{getScopeBadge(template.scope)}</TableCell>
                          <TableCell>
                            <span className="text-sm text-foreground">
                              {getItemsCount(template)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-foreground">
                              <div>{stats.timesApplied} times</div>
                              {stats.avgCompletion > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  {stats.avgCompletion.toFixed(1)}% avg
                                  completion
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end space-x-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  handleDuplicateTemplate(template)
                                }
                                className="text-muted-foreground hover:text-foreground hover:bg-muted"
                              >
                                <Copy className="h-4 w-4" />
                                <span className="sr-only">Duplicate</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditTemplate(template)}
                                className="text-muted-foreground hover:text-foreground hover:bg-muted"
                              >
                                <Edit className="h-4 w-4" />
                                <span className="sr-only">Edit</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenItemManager(template)}
                                className="text-muted-foreground hover:text-primary hover:bg-primary/10"
                              >
                                <Settings className="h-4 w-4" />
                                <span className="sr-only">Edit Items</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedTemplate(template);
                                  setIsDeletePanelOpen(true);
                                }}
                                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span className="sr-only">Delete</span>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Create Template Panel */}
          <SlidePanel
            isOpen={isCreatePanelOpen}
            onClose={() => setIsCreatePanelOpen(false)}
            title="Create New Template"
            width="lg"
            topOffset="64px"
            showEdgeCloseButton
            footer={
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setIsCreatePanelOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleCreateTemplate}>Create Template</Button>
              </div>
            }
          >
            <div className="space-y-6">
              <p className="text-muted-foreground">
                Create a new checklist template for your pipeline stages.
              </p>

              <div className="space-y-4">
                <FormField label="Template Name">
                  <Input
                    value={newTemplate.name}
                    onChange={(e) =>
                      setNewTemplate({ ...newTemplate, name: e.target.value })
                    }
                    placeholder="Enter template name"
                  />
                </FormField>
                <FormField label="Description">
                  <Textarea
                    value={newTemplate.description}
                    onChange={(e) =>
                      setNewTemplate({
                        ...newTemplate,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter template description"
                    rows={3}
                  />
                </FormField>
                <FormField label="Category">
                  <Input
                    value={newTemplate.category}
                    onChange={(e) =>
                      setNewTemplate({
                        ...newTemplate,
                        category: e.target.value,
                      })
                    }
                    placeholder="e.g., intake, design, install"
                  />
                </FormField>
                <FormField label="Pipeline">
                  <Select
                    value={newTemplate.pipeline_id?.toString() || "none"}
                    onValueChange={(value) =>
                      setNewTemplate({
                        ...newTemplate,
                        pipeline_id: value === "none" ? null : parseInt(value),
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select pipeline (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No pipeline</SelectItem>
                      {pipelines.map((pipeline) => (
                        <SelectItem
                          key={pipeline.id}
                          value={pipeline.id.toString()}
                        >
                          {pipeline.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Stage Name">
                  <Input
                    value={newTemplate.stage_name}
                    onChange={(e) =>
                      setNewTemplate({
                        ...newTemplate,
                        stage_name: e.target.value,
                      })
                    }
                    placeholder="e.g., Project Intake, Design Review"
                  />
                </FormField>
                <FormField label="Scope">
                  <Select
                    value={newTemplate.scope}
                    onValueChange={(value: "global" | "tenant" | "shared") =>
                      setNewTemplate({ ...newTemplate, scope: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tenant">Tenant (Private)</SelectItem>
                      <SelectItem value="shared">Shared</SelectItem>
                      <SelectItem value="global">Global</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              </div>
            </div>
          </SlidePanel>

          {/* Edit Template Panel */}
          <SlidePanel
            isOpen={isEditPanelOpen}
            onClose={() => setIsEditPanelOpen(false)}
            title="Edit Template"
            width="lg"
            topOffset="64px"
            showEdgeCloseButton
            footer={
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setIsEditPanelOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleUpdateTemplate}>Update Template</Button>
              </div>
            }
          >
            <div className="space-y-6">
              <p className="text-muted-foreground">
                Update the template details.
              </p>

              <div className="space-y-4">
                <FormField label="Template Name">
                  <Input
                    value={newTemplate.name}
                    onChange={(e) =>
                      setNewTemplate({ ...newTemplate, name: e.target.value })
                    }
                    placeholder="Enter template name"
                  />
                </FormField>
                <FormField label="Description">
                  <Textarea
                    value={newTemplate.description}
                    onChange={(e) =>
                      setNewTemplate({
                        ...newTemplate,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter template description"
                    rows={3}
                  />
                </FormField>
                <FormField label="Category">
                  <Input
                    value={newTemplate.category}
                    onChange={(e) =>
                      setNewTemplate({
                        ...newTemplate,
                        category: e.target.value,
                      })
                    }
                    placeholder="e.g., intake, design, install"
                  />
                </FormField>
                <FormField label="Pipeline">
                  <Select
                    value={newTemplate.pipeline_id?.toString() || "none"}
                    onValueChange={(value) =>
                      setNewTemplate({
                        ...newTemplate,
                        pipeline_id: value === "none" ? null : parseInt(value),
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select pipeline (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No pipeline</SelectItem>
                      {pipelines.map((pipeline) => (
                        <SelectItem
                          key={pipeline.id}
                          value={pipeline.id.toString()}
                        >
                          {pipeline.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Stage Name">
                  <Input
                    value={newTemplate.stage_name}
                    onChange={(e) =>
                      setNewTemplate({
                        ...newTemplate,
                        stage_name: e.target.value,
                      })
                    }
                    placeholder="e.g., Project Intake, Design Review"
                  />
                </FormField>
                <FormField label="Scope">
                  <Select
                    value={newTemplate.scope}
                    onValueChange={(value: "global" | "tenant" | "shared") =>
                      setNewTemplate({ ...newTemplate, scope: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tenant">Tenant (Private)</SelectItem>
                      <SelectItem value="shared">Shared</SelectItem>
                      <SelectItem value="global">Global</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              </div>
            </div>
          </SlidePanel>

          {/* Delete Template Panel */}
          <SlidePanel
            isOpen={isDeletePanelOpen}
            onClose={() => setIsDeletePanelOpen(false)}
            title="Delete Template"
            width="md"
            topOffset="64px"
            showEdgeCloseButton
            footer={
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setIsDeletePanelOpen(false)}
                >
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDeleteTemplate}>
                  Delete Template
                </Button>
              </div>
            }
          >
            <div className="space-y-4">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <h4 className="font-medium text-destructive mb-2">Confirm Deletion</h4>
                <p className="text-muted-foreground">
                  Are you sure you want to delete "{selectedTemplate?.name}"?
                  This action cannot be undone.
                </p>
              </div>
            </div>
          </SlidePanel>

          {/* Template Item Manager */}
          {showItemManager && selectedTemplate && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              onClick={(e) => {
                // Close when clicking on the backdrop
                if (e.target === e.currentTarget) {
                  setShowItemManager(false);
                  setSelectedTemplate(null);
                  // Don't reload templates when just closing the dialog
                }
              }}
            >
              <div className="bg-background rounded-lg p-6 max-w-6xl w-full max-h-[95vh] overflow-y-auto border shadow-lg">
                <TemplateItemManager
                  templateId={selectedTemplate.id}
                  templateName={selectedTemplate.name}
                  onCancel={() => {
                    setShowItemManager(false);
                    setSelectedTemplate(null);
                    // Refresh templates to get updated item counts after editing
                    fetchTemplates();
                  }}
                />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="contracts" className="space-y-6">
          <ContractTemplatesTab />
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <NotificationTemplateManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
};
